import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkStepper from "../../components/atoms/TkStepper";
import TkBackButton from "../../components/molecules/TkBackButton";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import { httpAPI_RET017, httpAPI_RET018 } from "./api/request";
import { fakeTableList, schema, stepper } from "./parts";
import { Box } from "@mui/material";
import { Prefectures } from "../../assets/data/Constants";
import { TkGetCode } from "../../utils/TkGetCode";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { IAPI_RET018_Request } from "../../type/apiType";
import { API_RET018_BuildReqParams } from "../../utils/buildRequestParams";
import _ from "lodash";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";

/**
 * SCRR411119 退職手当金受領者の変更手続き（連絡先の登録）
 * @return SCRR411119
 */
const SCRR411119 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.job_40;
  const userType = state.authInfo?.userType;
  const isUserTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;
  const staffNo = location.state?.staffNo ?? context?.staffNo;
  const staffName = location.state?.staffName ?? context?.retirementStaffName;
  const contractorNo = isUserTypeKiko
    ? businessState?.common?.contractorNo
    : state.authInfo?.contractorNo;
  const [statecorpInfoList, setStatecorpInfoList] = useState<any>([]);
  const [tel, setTel] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  // 初期表示
  const handleInit = async () => {
    try {
      let resArr = stateFakeTableList.current;
      if (isUserTypeKiko) {
        let retirementStaffName = resArr.find(
          (item) => item.keyword === "retirementStaffName"
        )!;
        retirementStaffName.showLine = true;
        retirementStaffName.contractorNumber =
          (staffNo ?? "") + ":" + (staffName ?? "");
      }
      let address = resArr.find((item) => item.keyword === "address")!;
      let demanderZipcode = resArr.find(
        (item) => item.keyword === "demanderZipcode"
      )!;
      demanderZipcode.getPostalCodeValue = (value: any) => {
        const data = value.value;

        let resObj = {
          prefectures: data.address1,
          city: `${data.address2}${data.address3}`,
          address: "",
        };

        address.valueObject = resObj;
        setState({
          ...state,
          errorMsgId: "",
        });
      };

      demanderZipcode.getPostalCodeError = (value: any) => {
        setState({
          ...state,
          errorMsgId: value,
        });
      };

      // 受領者変更参照API呼び出し
      const res = await httpAPI_RET017({
        staffNo,
        contractorNo,
        applyId: location.state?.applyId,
      });

      if (res.resultCd === "I0000") {
        for (let i in res) {
          let targetObj = resArr.find((item) => item.keyword === i)!;
          targetObj && (targetObj.setDefaultValue = res[i as keyof typeof res]);
        }

        address.valueObject = {
          prefectures: TkGetCode(Prefectures, res.demanderPrefectureCd),
          city: res.demanderCity ?? "",
          address: res.demanderAddressOthers ?? "",
        };

        let demanderEmailConfirm = resArr.find(
          (item) => item.keyword === "demanderEmailConfirm"
        )!;
        demanderEmailConfirm.setDefaultValue = res?.demanderEmail;

        setBusinessState({
          ...businessState,
          job_40: {
            ...context,
            tempSaveStep: res.tempSaveStep,
            applyId: res.applyId,
            staffNo,
            retirementStaffName: staffName,
            demanderKbn: res.demanderKbn,
            demanderRelationship: res.demanderRelationship,
            demanderAddressOthers: res.demanderRelationshipOthers,
            agentEmail: res.agentEmail,
            demanderFlg: res.demanderFlg,
            livelihoodFromRetireeFlg: res.livelihoodFromRetireeFlg,
            fileList: res.fileList,
          },
        });
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res?.resultCd,
        });
      }
      setStatecorpInfoList(resArr);
    } catch (error) {
      // エラー内容を表示する。
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let tel = resArr.find((item) => item.keyword === "tel")!;

    tel.onGetInputValue = (value: any) => {
      setTel(value.target.valueForShow);
    };
  });

  const makeHttpObj = (data: any): IAPI_RET018_Request => {
    const reqObj: IAPI_RET018_Request = {
      ...context,
      ...data,
      tempSaveStep: 1,
      contractorNo,
      staffNo,
      screenId: "SCRR411119",
    };

    return API_RET018_BuildReqParams(reqObj);
  };

  const onCheckAndSubmit = async (data: any, index?: number) => {
    // 業務チェック
    if (data.demanderEmail !== data.demanderEmailConfirm) {
      setState({
        ...state,
        errorMsgId: "TKMSG_SCR0001E",
      });
      return;
    }

    const dataObj = {
      demanderName: data.demanderName,
      demanderNameKana: data.demanderNameKana,
      demanderBirth: data.demanderBirth,
      demanderZipcode: data.demanderZipcode,
      demanderPrefectureCd:
        Prefectures[data.address?.prefectures as keyof typeof Prefectures],
      demanderCity: data.address?.city,
      demanderAddressOthers: data.address?.address,
      tel,
      demanderEmail: data.demanderEmail,
      staffNo,
      retirementStaffName: staffName,
    };

    if (index === 0) {
      try {
        // 受領者変更更新API
        const res = await httpAPI_RET018({
          ...makeHttpObj(dataObj),
        });

        if (res.resultCd === "I0000") {
          setState({
            ...state,
            errorMsgId: "",
          });
          setBusinessState({
            ...businessState,
            job_40: {
              ...context,
              ...dataObj,
              applyId: res.applyId,
              tempSaveStep: 1,
            },
          });
          setOpenSuccess(true);
        } else {
          setState({
            ...state,
            errorMsgId: res.resultCd,
          });
        }
      } catch (error) {
        navigate("/error");
      }
    } else {
      setBusinessState({
        ...businessState,
        job_40: {
          ...context,
          ...dataObj,
        },
      });
      navigate("/SCRR411120");
    }
  };

  return (
    <Box className="SCRR411119" data-testid="SCRR411119PageForTest">
      <TkStepper
        steps={stepper}
        activeStep={0}
        savaStep={context?.tempSaveStep}
      />

      <TkFakeTable
        list={statecorpInfoList}
        ruleSchema={schema}
        onCheckAndSubmit={(data, index) => {
          onCheckAndSubmit(data, index);
        }}
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
      />

      <TkMessageDialog
        open={openSuccess}
        onOky={() => setOpenSuccess(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRR411119;
